import { api, ApiError } from '../api.js'
import { isHistoryInstant } from '../dateTime/historyDateTime.js'
import { paginationItems } from './transactions.js'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const pageSizes = new Set([25, 50, 100])
const statusModes = new Set(['ALL', 'OK', 'ERROR'])
const alarmModes = new Set(['ALL', 'PRESENT', 'ABSENT'])
const completenessModes = new Set(['ALL', 'INCOMPLETE'])
const facets = new Set(['TANK', 'FUEL'])
const alarmCodes = new Set(['CriticalHighProduct', 'HighProduct', 'LowProduct', 'CriticalLowProduct', 'HighWater', 'TankLeakage'])
const displayStates = new Set(['NORMAL', 'ALARM', 'ERROR', 'INCOMPLETE'])
const alarmStates = new Set(['KNOWN', 'PARTIAL', 'UNAVAILABLE'])
const maximumRangeMilliseconds = 366 * 24 * 60 * 60 * 1000
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const invalid = (kind = 'response') => new ApiError(`Invalid PTS-2 tank measurement ${kind}`,
  kind === 'query' ? 400 : 502, `pts2_tank_measurement_${kind}_invalid`)

function positive(value, maximum = Number.MAX_SAFE_INTEGER) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 1 || number > maximum) throw invalid('query')
  return number
}

function positiveSet(value) {
  if (value == null) return []
  if (!Array.isArray(value) || value.length > 20) throw invalid('query')
  const result = value.map((item) => positive(item, 20))
  if (new Set(result).size !== result.length) throw invalid('query')
  return result
}

function iso(value, exclusiveEnd = false) {
  const date = new Date(value)
  if (!isHistoryInstant(value, { exclusiveEnd })) throw invalid('query')
  return date.toISOString()
}

export function tankMeasurementRangeIssue(from, to) {
  const start = Date.parse(from), end = Date.parse(to)
  if (!isHistoryInstant(from) || !isHistoryInstant(to, { exclusiveEnd: true })) return 'REQUIRED'
  if (start >= end) return 'ORDER'
  if (end - start > maximumRangeMilliseconds) return 'TOO_LONG'
  return null
}

export function normalizeTankMeasurementFilters(value = {}) {
  const result = {
    stationId: positive(value.stationId),
    controllerId: positive(value.controllerId),
    from: iso(value.from),
    to: iso(value.to, true),
    tankNumbers: positiveSet(value.tankNumbers),
    fuelGradeIds: positiveSet(value.fuelGradeIds),
    statusMode: value.statusMode || 'ALL',
    alarmMode: value.alarmMode || 'ALL',
    completenessMode: value.completenessMode || 'ALL',
  }
  if (!statusModes.has(result.statusMode) || !alarmModes.has(result.alarmMode)
    || !completenessModes.has(result.completenessMode) || tankMeasurementRangeIssue(result.from, result.to)) {
    throw invalid('query')
  }
  return result
}

export function filterPayload(value) {
  return Object.fromEntries(Object.entries(normalizeTankMeasurementFilters(value)).filter(([, item]) => item !== 'ALL'
    && !(Array.isArray(item) && item.length === 0)))
}

function queryFor(filters, page, limit) {
  page = positive(page, 10000)
  limit = Number(limit)
  if (!pageSizes.has(limit)) throw invalid('query')
  const payload = filterPayload(filters)
  const query = new URLSearchParams(Object.fromEntries(Object.entries(payload).filter(([, value]) => !Array.isArray(value))))
  for (const field of ['tankNumbers', 'fuelGradeIds']) if (payload[field]?.length) query.set(field, payload[field].join(','))
  query.set('page', String(page)); query.set('limit', String(limit))
  return query
}

function decimal(value) {
  return value === null || typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)
}

function measurementRow(value, stationId, controllerId) {
  if (!isRecord(value) || !uuid.test(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || !(value.tankNumber === null || Number.isSafeInteger(value.tankNumber) && value.tankNumber >= 1 && value.tankNumber <= 20)
    || !(value.fuelGradeId === null || Number.isSafeInteger(value.fuelGradeId) && value.fuelGradeId >= 1 && value.fuelGradeId <= 20)
    || !(value.status === null || ['OK', 'ERROR'].includes(value.status)) || !Array.isArray(value.alarms)
    || value.alarms.some((item) => !alarmCodes.has(item)) || !alarmStates.has(value.alarmDataState)
    || typeof value.dataComplete !== 'boolean' || !displayStates.has(value.displayState)
    || !['productVolume', 'productHeight', 'waterHeight', 'temperature'].every((field) => decimal(value[field]))) {
    throw invalid()
  }
  return structuredClone(value)
}

function summary(value) {
  if (!isRecord(value) || !['all', 'withAlarms', 'errors', 'incomplete']
    .every((field) => Number.isSafeInteger(value[field]) && value[field] >= 0)) throw invalid()
  return structuredClone(value)
}

export async function getPts2TankMeasurements(filters, { page = 1, limit = 25, signal } = {}) {
  filters = normalizeTankMeasurementFilters(filters)
  const value = await api(`/pts2/tank-measurements/browse?${queryFor(filters, page, limit)}`, { signal })
  if (!isRecord(value) || !Array.isArray(value.items) || value.items.length > limit
    || !Number.isSafeInteger(value.page) || value.page < 1 || value.limit !== Number(limit)
    || !Number.isSafeInteger(value.totalItems) || value.totalItems < 0
    || !Number.isSafeInteger(value.totalPages) || value.totalPages < 0) throw invalid()
  return { ...value, summary: summary(value.summary),
    items: value.items.map((item) => measurementRow(item, filters.stationId, filters.controllerId)) }
}

export async function getPts2TankMeasurementOptions(filters, facet, { signal } = {}) {
  const payload = filterPayload(filters)
  if (!facets.has(facet)) throw invalid('query')
  const value = await api('/pts2/tank-measurements/filter-options', {
    method: 'POST', signal, body: JSON.stringify({ filters: payload, facet }),
  })
  if (!isRecord(value) || value.facet !== facet || !Array.isArray(value.items) || value.items.length > 20
    || typeof value.hasUnassigned !== 'boolean' || value.items.some((item) => !isRecord(item)
      || !Number.isSafeInteger(item.number) || item.number < 1 || item.number > 20
      || !(item.name === null || typeof item.name === 'string'))) throw invalid()
  return structuredClone(value)
}

export async function getPts2TankMeasurementDetail(stationId, controllerId, measurementId, { signal } = {}) {
  stationId = positive(stationId); controllerId = positive(controllerId)
  if (!uuid.test(measurementId)) throw invalid('query')
  const value = await api(`/pts2/tank-measurements/${measurementId}?stationId=${stationId}&controllerId=${controllerId}`, { signal })
  if (!isRecord(value) || !isRecord(value.measurement) || value.packetType !== 'UploadTankMeasurement') throw invalid()
  value.measurement = measurementRow(value.measurement, stationId, controllerId)
  for (const field of ['productUllage', 'productTcVolume', 'waterVolume', 'productDensity', 'productMass', 'fillingPercentage']) {
    if (!decimal(value[field])) throw invalid()
  }
  return structuredClone(value)
}

export { paginationItems }
